/**
 * Seção de PLANOS CONCLUÍDOS (GUPM-332) — rodapé da carteira MPA.
 *
 * Mesmo padrão do `TarpsFechadasPanel` do M3: colapsável, fechada por padrão,
 * filtros PRÓPRIOS (independentes da toolbar global) e some quando não há nada
 * concluído. Recebe os grupos derivados da carteira COMPLETA — os filtros
 * globais governam a lista ativa, não este painel.
 *
 * Read-only: um plano 100% concluído não tem status a avançar, então os cards
 * vêm com `canEdit={false}` mesmo para RT/admin. Clicar no card segue
 * abrindo o `PlanoDetail`.
 */
import { useEffect, useMemo, useState } from 'react';
import { LEVEL_UI, type TarpLevel } from '@/ui';
import type { GrupoPlano } from '@/domain/planos/mpa';
import { MpaPlanoCard } from './MpaPlanoCard';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';

const SELECT_CLS = 'rounded-lg border border-jf-border bg-jf-surface2 px-2 py-1 text-xs text-white';

interface Props {
  grupos: GrupoPlano[];
  hoje: Date;
  /**
   * O usuário pediu concluídas na toolbar global (`status=concluido`). A seção não
   * responde aos filtros globais — tem os próprios, como o `TarpsFechadasPanel` —,
   * então sem isto o chip "Concluído" ficava órfão: a pill contava as concluídas e a
   * lista principal respondia "nenhuma ação", com o item escondido aqui dentro.
   * Achado na revisão do PR #212.
   */
  abrirPorPedidoExplicito: boolean;
  onOpenPlano: (planoId: string) => void;
}

// Os cards daqui são sempre read-only (`canEdit={false}`): o callback de avanço
// nunca é chamado, e o do card é obrigatório.
const semAvanco = () => {};

export function PlanosConcluidosPanel({
  grupos,
  hoje,
  abrirPorPedidoExplicito,
  onOpenPlano,
}: Props) {
  const [open, setOpen] = useState(false);
  const [damCode, setDamCode] = useState('todas');
  const [nivel, setNivel] = useState('todos');

  // Só ABRE — nunca fecha. Fechar por mudança de filtro tiraria da tela um painel
  // que o usuário abriu na mão.
  useEffect(() => {
    if (abrirPorPedidoExplicito) setOpen(true);
  }, [abrirPorPedidoExplicito]);

  const damCodes = useMemo(
    () => [...new Set(grupos.map((g) => g.damCode).filter(Boolean))].sort(),
    [grupos],
  );
  // Níveis DERIVADOS dos planos presentes — oferecer os 3 fixos criaria opções que
  // não filtram nada. Planos sem nível seguem alcançáveis por "Todos os níveis".
  const niveis = useMemo(
    () => [...new Set(grupos.map((g) => g.nivel).filter((n): n is string => !!n))].sort(),
    [grupos],
  );
  const lista = useMemo(
    () =>
      grupos.filter(
        (g) => (damCode === 'todas' || g.damCode === damCode) && (nivel === 'todos' || g.nivel === nivel),
      ),
    [grupos, damCode, nivel],
  );

  // Sem planos concluídos → a seção não existe (não polui a tela).
  if (grupos.length === 0) return null;

  return (
    <section aria-label="Planos concluídos" className="mt-3 rounded-xl border border-jf-border bg-jf-surface">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-white/[0.03]"
      >
        <span className="text-sm font-bold text-jf-muted">
          ✅ Planos concluídos — {grupos.length} plano{grupos.length > 1 ? 's' : ''}
        </span>
        <span className="text-xs text-jf-dim">{open ? 'ocultar ▴' : 'mostrar ▾'}</span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-jf-border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Filtrar concluídos por estrutura"
              className={SELECT_CLS}
              value={damCode}
              onChange={(e) => setDamCode(e.target.value)}
            >
              <option value="todas">Todas as estruturas</option>
              {damCodes.map((c) => (
                <option key={c} value={c}>
                  {rotuloDaEstrutura(c)}
                </option>
              ))}
            </select>
            <select
              aria-label="Filtrar concluídos por nível"
              className={SELECT_CLS}
              value={nivel}
              onChange={(e) => setNivel(e.target.value)}
            >
              <option value="todos">Todos os níveis</option>
              {niveis.map((n) => (
                <option key={n} value={n}>
                  {n in LEVEL_UI ? LEVEL_UI[n as TarpLevel].label : n}
                </option>
              ))}
            </select>
          </div>

          {lista.length === 0 ? (
            <p className="py-2 text-center text-xs text-jf-muted">Nenhum plano concluído com esses filtros.</p>
          ) : (
            <div className="space-y-2">
              {lista.map((g) => (
                <MpaPlanoCard
                  key={g.planoId}
                  grupo={g}
                  hoje={hoje}
                  canEdit={false}
                  updating={false}
                  onOpenPlano={onOpenPlano}
                  onUpdateAcao={semAvanco}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
